import type { DatosDePlaca } from './placa';
import marca from '../nucleo/marca.generated.json';

export const DISENOS = [
  { id: 'trofeo', nombre: 'Trofeo', detalle: 'Una celebración en dorado', muestra: '01' },
  { id: 'foto', nombre: 'Con foto', detalle: 'La pareja, protagonista', muestra: '02' },
  { id: 'grafica', nombre: 'Gráfica', detalle: 'Energía de cancha', muestra: '03' },
  { id: 'completa', nombre: 'Resumen', detalle: 'Podio y camino al título', muestra: '04' },
  { id: 'propia', nombre: 'Diseño propio', detalle: 'Tu imagen, sin agregados', muestra: '05' },
] as const;
export type DisenoPlaca = (typeof DISENOS)[number]['id'];
export const PALETAS = {
  azul: { nombre: 'Noche azul', fondo: '#031b3d', acento: '#16c6ee' },
  verde: { nombre: 'Cancha verde', fondo: '#062d27', acento: '#9be8b3' },
  negro: { nombre: 'Negro y oro', fondo: '#11151b', acento: '#e6b84a' },
} as const;
export type PaletaPlaca = keyof typeof PALETAS;
export interface OpcionesPlaca {
  diseno: DisenoPlaca;
  paleta: PaletaPlaca;
  foto: string | null;
  propia: string | null;
  encuadreX: number;
  encuadreY: number;
  sponsors: { nombre: string; src: string }[];
}
export const OPCIONES_INICIALES: OpcionesPlaca = {
  diseno: 'trofeo', paleta: 'azul', foto: null, propia: null,
  encuadreX: 50, encuadreY: 50, sponsors: [],
};
export const MAX_SPONSORS = 6;
export const MAX_ARCHIVO = 12 * 1024 * 1024;

export function preferenciasGuardadas(): Pick<OpcionesPlaca, 'diseno' | 'paleta'> {
  try {
    const p = JSON.parse(localStorage.getItem('encancha.placa.estilo') ?? '{}') as Record<string, unknown>;
    return {
      diseno: DISENOS.some(d => d.id === p.diseno) ? p.diseno as DisenoPlaca : 'trofeo',
      paleta: typeof p.paleta === 'string' && Object.hasOwn(PALETAS, p.paleta) ? p.paleta as PaletaPlaca : 'azul',
    };
  } catch { return { diseno: 'trofeo', paleta: 'azul' }; }
}

export function cargarImagen(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('No se pudo abrir la imagen. Probá con JPG, PNG o WebP.'));
    img.src = src;
  });
}

/** Archivos locales normalizados; nunca se envían al servidor ni se guardan en localStorage. */
export async function leerImagen(file: File, maxLado = 1800): Promise<string> {
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) throw new Error('Elegí una imagen JPG, PNG o WebP.');
  if (file.size > MAX_ARCHIVO) throw new Error('La imagen supera los 12 MB. Elegí una más liviana.');
  const url = URL.createObjectURL(file);
  try {
    const img = await cargarImagen(url);
    if (img.naturalWidth * img.naturalHeight > 40_000_000) throw new Error('La imagen es demasiado grande. Exportala con menos resolución.');
    const escala = Math.min(1, maxLado / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(img.naturalWidth * escala));
    c.height = Math.max(1, Math.round(img.naturalHeight * escala));
    c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
    return c.toDataURL('image/png');
  } finally { URL.revokeObjectURL(url); }
}

export function aPng(c: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => c.toBlob(b => b ? resolve(b) : reject(new Error('No se pudo exportar la placa.')), 'image/png'));
}

function cubrir(c: CanvasRenderingContext2D, img: HTMLImageElement, x: number, y: number, w: number, h: number, fx = 50, fy = 50) {
  const escala = Math.max(w / img.naturalWidth, h / img.naturalHeight);
  const sw = w / escala, sh = h / escala;
  c.drawImage(img, (img.naturalWidth - sw) * fx / 100, (img.naturalHeight - sh) * fy / 100, sw, sh, x, y, w, h);
}

/** Texto en líneas reales: sin deformar nombres largos con el maxWidth de canvas. */
function texto(c: CanvasRenderingContext2D, valor: string, y: number, tam: number, maxLineas = 1, fuente = '"Bebas Neue"', color = '#f7f5ee'): number {
  let lineas: string[] = [];
  for (let t = tam; t >= 20; t -= 2) {
    c.font = `${t}px ${fuente}`;
    lineas = [''];
    for (const palabra of valor.split(/\s+/)) {
      const ultima = lineas.length - 1;
      const prueba = `${lineas[ultima]} ${palabra}`.trim();
      if (c.measureText(prueba).width > 920 && lineas[ultima]) lineas.push(palabra);
      else lineas[ultima] = prueba;
    }
    if (lineas.length <= maxLineas && lineas.every(l => c.measureText(l).width <= 920)) {
      c.fillStyle = color;
      for (const [i, l] of lineas.entries()) c.fillText(l, 540, y + i * t * 1.1);
      return y + lineas.length * t * 1.1;
    }
  }
  throw new Error('Hay un texto demasiado largo para esta plantilla. Elegí Resumen.');
}

export async function dibujarGaleria(d: DatosDePlaca, o: OpcionesPlaca): Promise<Blob> {
  if (o.diseno === 'propia') {
    if (!o.propia) throw new Error('Subí tu diseño para preparar la imagen.');
    const img = await cargarImagen(o.propia);
    const c = document.createElement('canvas');
    c.width = img.naturalWidth; c.height = img.naturalHeight;
    c.getContext('2d')!.drawImage(img, 0, 0);
    return aPng(c);
  }
  if (o.diseno === 'foto' && !o.foto) throw new Error('Subí una foto de la pareja para usar este diseño.');
  await Promise.all([document.fonts.load('96px "Bebas Neue"'), document.fonts.load('600 32px "Titillium Web"')]);
  const [hero, logos, logoMarca] = await Promise.all([
    o.diseno === 'trofeo' ? cargarImagen('/placas/trofeo.png') : o.diseno === 'foto' ? cargarImagen(o.foto!) : Promise.resolve(null),
    Promise.all(o.sponsors.slice(0, MAX_SPONSORS).map(s => cargarImagen(s.src))),
    cargarImagen(marca.logo),
  ]);
  const c = document.createElement('canvas');
  c.width = 1080; c.height = 1350;
  const ctx = c.getContext('2d')!;
  const paleta = PALETAS[o.paleta];
  ctx.fillStyle = paleta.fondo; ctx.fillRect(0, 0, 1080, 1350);
  if (hero && o.diseno === 'trofeo') {
    ctx.drawImage(hero, 0, 0, 1080, 1350);
    if (o.paleta !== 'azul') {
      ctx.globalCompositeOperation = 'color'; ctx.fillStyle = paleta.acento;
      ctx.globalAlpha = 0.18; ctx.fillRect(0, 0, 1080, 1350);
      ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
    }
  } else if (hero) {
    // Foto vertical a sangre: ocupa toda la placa, hasta la franja de sponsors.
    const altoFoto = logos.length ? 1170 : 1350;
    cubrir(ctx, hero, 0, 0, 1080, altoFoto, o.encuadreX, o.encuadreY);
    // La zona central queda limpia; solo protegemos el encabezado y los datos al pie.
    const cabecera = ctx.createLinearGradient(0, 0, 0, 300);
    cabecera.addColorStop(0, '#020b18c7');
    cabecera.addColorStop(0.65, '#020b1859');
    cabecera.addColorStop(1, '#020b1800');
    ctx.fillStyle = cabecera;
    ctx.fillRect(0, 0, 1080, 300);
    const pie = ctx.createLinearGradient(0, 985, 0, altoFoto);
    pie.addColorStop(0, '#020b1800');
    pie.addColorStop(0.4, '#020b1880');
    pie.addColorStop(1, '#020b18cc');
    ctx.fillStyle = pie;
    ctx.fillRect(0, 985, 1080, altoFoto - 985);
  } else {
    const luz = ctx.createRadialGradient(950, 520, 0, 700, 600, 800);
    luz.addColorStop(0, `${paleta.acento}88`); luz.addColorStop(1, `${paleta.fondo}00`);
    ctx.fillStyle = luz; ctx.fillRect(0, 0, 1080, 1100);
    ctx.save(); ctx.translate(540, 600); ctx.rotate(-0.16);
    ctx.strokeStyle = `${paleta.acento}88`; ctx.lineWidth = 3;
    ctx.strokeRect(-430, -170, 860, 390); ctx.strokeRect(-300, -170, 600, 390);
    ctx.beginPath(); ctx.moveTo(-430, 25); ctx.lineTo(430, 25); ctx.stroke();
    ctx.textAlign = 'center'; ctx.fillStyle = '#ffffff16'; ctx.font = '480px "Bebas Neue"'; ctx.fillText('01', 0, 175);
    ctx.restore();
    ctx.beginPath(); ctx.arc(850, 520, 58, 0, Math.PI * 2); ctx.fillStyle = paleta.acento; ctx.fill();
  }
  ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
  ctx.drawImage(logoMarca, 400, 18, 280, 56);
  texto(ctx, d.torneo.toUpperCase(), 105, 42, 1);
  texto(ctx, d.categoria.toUpperCase(), 148, 32, 1, '"Titillium Web"', paleta.acento);
  texto(ctx, 'PAREJA CAMPEONA', 250, 112);
  // Protección ajustada a cada nombre: conserva visible el fondo alrededor.
  // El halo y el contorno fino separan las letras de los reflejos de la foto.
  let y = 875;
  for (const nombre of d.campeona.split(' / ')) {
    const valor = nombre.toUpperCase();
    ctx.save();
    ctx.shadowColor = '#020b18';
    ctx.shadowBlur = 10;
    ctx.shadowOffsetY = 2;
    const siguiente = texto(ctx, valor, y, 78, 1);
    const ancho = ctx.measureText(valor).width;
    const tamanio = parseFloat(ctx.font);
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;
    ctx.save();
    ctx.translate(540, y - tamanio * 0.35);
    ctx.scale(ancho / 2 + 35, tamanio * 0.72);
    // El degradado se crea en coordenadas normalizadas para dibujar una elipse.
    const elipse = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
    elipse.addColorStop(0, '#031b3d80');
    elipse.addColorStop(0.72, '#031b3d65');
    elipse.addColorStop(1, '#031b3d00');
    ctx.fillStyle = elipse;
    ctx.fillRect(-1, -1, 2, 2);
    ctx.restore();
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = '#031b3db3';
    ctx.strokeText(valor, 540, y);
    ctx.fillStyle = '#f7f5ee';
    ctx.fillText(valor, 540, y);
    ctx.restore();
    y = siguiente;
  }
  const final = d.camino.find(p => p.ronda === 'Final');
  if (final) {
    texto(ctx, 'FINAL', 1040, 27, 1, '"Titillium Web"', paleta.acento);
    texto(ctx, final.marcador, 1094, 57);
  }
  texto(ctx, d.cuando, 1140, 25, 1, '"Titillium Web"');
  if (logos.length) {
    ctx.fillStyle = '#faf9f5'; ctx.fillRect(0, 1170, 1080, 180);
    texto(ctx, 'ACOMPAÑAN', 1196, 20, 1, '"Titillium Web"', '#203348');
    const cols = Math.min(3, logos.length), rows = Math.ceil(logos.length / 3);
    const alto = rows === 1 ? 88 : 53;
    logos.forEach((img, i) => {
      const row = Math.floor(i / 3), count = Math.min(cols, logos.length - row * 3);
      const cw = 920 / count;
      const scale = Math.min((cw - 45) / img.naturalWidth, alto / img.naturalHeight);
      const w = img.naturalWidth * scale, h = img.naturalHeight * scale;
      ctx.drawImage(img, 80 + (i % 3) * cw + (cw - w) / 2, 1210 + row * 64 + (alto - h) / 2, w, h);
    });
  } else {
    ctx.strokeStyle = `${paleta.acento}66`; ctx.beginPath(); ctx.moveTo(80, 1210); ctx.lineTo(1000, 1210); ctx.stroke();
    texto(ctx, d.link.replace(/^https?:\/\//, ''), 1280, 26, 1, '"Titillium Web"', '#e0e7ee');
  }
  return aPng(c);
}
